import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowRight, Zap, Clock, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, EVENTS } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const USE_CASES = ['DG replacement', 'Backup power', 'Peak shaving', 'Solar shifting', 'UPS / Critical loads', 'EV Charging', 'Other'];

const USE_CASE_MAP = {
  dg_replacement: 'DG replacement',
  backup: 'Backup power',
  peak_shaving: 'Peak shaving',
  solar_shifting: 'Solar shifting',
  ups: 'UPS / Critical loads',
  ev_charging: 'EV Charging',
};

const SECTOR_LABELS = {
  restaurants: 'Restaurant / Hospitality',
  hospitals: 'Hospital / Healthcare',
  campuses: 'University / Campus',
  warehouses: 'Warehouse / Industrial',
  apartments: 'Residential / Apartment',
  ev_charging: 'EV Charging',
  solar: 'Solar Installation',
  general: 'General / Commercial',
};

export default function QuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('quick');
  const [submitting, setSubmitting] = useState(false);

  const sectorParam = searchParams.get('sector') || '';
  const useCaseParam = searchParams.get('use_case') || '';
  const prefilledUseCase = USE_CASE_MAP[useCaseParam] || '';
  const prefilledSectorLabel = SECTOR_LABELS[sectorParam] || '';

  const [form, setForm] = useState({
    use_case: prefilledUseCase, backup_hours: 2, dg_rating_kva: 125,
    has_dg: true, has_solar: useCaseParam === 'solar_shifting', solar_kw: 0,
    city: '', pincode: '', contact_name: '', contact_email: '', contact_phone: '', company: '',
    notes: prefilledSectorLabel ? `Sector: ${prefilledSectorLabel}` : '',
    sector: sectorParam,
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.contact_email) {
      toast.error('Please provide your name and email.');
      return;
    }
    setSubmitting(true);
    trackEvent(EVENTS.START_RFQ, { source: 'quote_page', mode });
    try {
      const res = await fetch(`${BACKEND_URL}/api/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.quote_id) {
        toast.success('Quote request submitted! Generating recommendations...');
        navigate(`/quote/results/${data.quote_id}`);
      }
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="quote-page">
      <section className="py-16 lg:py-24">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-6">
              Get a Quote
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
              Tell us your <span className="blue-gradient-text">energy needs</span>
            </h1>
            <p className="text-base lg:text-lg text-[#57534E] leading-relaxed max-w-2xl">
              Answer a few questions and we'll recommend the optimal BESS configuration for your facility.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-24" data-testid="quote-form-section">
        <div className="voltide-container max-w-4xl">
          {/* Mode Toggle */}
          <div className="flex gap-3 mb-8" data-testid="quote-mode-toggle">
            <button onClick={() => setMode('quick')} className={`flex-1 p-4 rounded-xl text-left transition-all ${mode === 'quick' ? 'bg-[#0061DD] text-white shadow-lg' : 'bg-white border border-stone-200 text-[#57534E]'}`} data-testid="mode-quick-btn">
              <Zap className={`w-5 h-5 mb-2 ${mode === 'quick' ? 'text-white' : 'text-[#0061DD]'}`} />
              <p className="font-bold text-sm">Quick Estimate</p>
              <p className={`text-xs mt-1 ${mode === 'quick' ? 'text-blue-100' : 'text-[#A8A29E]'}`}>~2 minutes. Get instant recommendations.</p>
            </button>
            <button onClick={() => setMode('upload')} className={`flex-1 p-4 rounded-xl text-left transition-all ${mode === 'upload' ? 'bg-[#0061DD] text-white shadow-lg' : 'bg-white border border-stone-200 text-[#57534E]'}`} data-testid="mode-upload-btn">
              <Upload className={`w-5 h-5 mb-2 ${mode === 'upload' ? 'text-white' : 'text-[#0061DD]'}`} />
              <p className="font-bold text-sm">Upload Documents</p>
              <p className={`text-xs mt-1 ${mode === 'upload' ? 'text-blue-100' : 'text-[#A8A29E]'}`}>More accurate. Upload bills & DG logs.</p>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="bento-card p-6 lg:p-8" data-testid="quote-form">
            {prefilledSectorLabel && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100 mb-6" data-testid="quote-prefilled-banner">
                <Badge className="rounded-full bg-[#0061DD] text-white text-xs">{prefilledSectorLabel}</Badge>
                <span className="text-sm text-[#57534E]">Form pre-filled based on your selection</span>
              </div>
            )}
            <div className="space-y-6">
              {/* Use Case */}
              <div>
                <Label className="text-sm font-medium text-[#1A1A1A]">Primary Use Case</Label>
                <Select value={form.use_case} onValueChange={v => update('use_case', v)}>
                  <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="quote-use-case-select">
                    <SelectValue placeholder="What do you need BESS for?" />
                  </SelectTrigger>
                  <SelectContent>
                    {USE_CASES.map(uc => <SelectItem key={uc} value={uc}>{uc}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* DG Info */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-stone-50">
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">Do you currently have a Diesel Generator?</p>
                  <p className="text-xs text-[#A8A29E]">Helps us size the BESS replacement</p>
                </div>
                <Switch checked={form.has_dg} onCheckedChange={v => update('has_dg', v)} data-testid="quote-has-dg-switch" />
              </div>

              {form.has_dg && (
                <div data-testid="quote-dg-rating-slider">
                  <div className="flex justify-between mb-2">
                    <Label className="text-sm font-medium text-[#1A1A1A]">DG Rating</Label>
                    <span className="mono text-sm font-semibold text-[#0061DD]">{form.dg_rating_kva} KVA</span>
                  </div>
                  <Slider value={[form.dg_rating_kva]} onValueChange={([v]) => update('dg_rating_kva', v)} min={25} max={1000} step={25} />
                  <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>25 KVA</span><span>1000 KVA</span></div>
                </div>
              )}

              {/* Backup Hours */}
              <div data-testid="quote-backup-hours-slider">
                <div className="flex justify-between mb-2">
                  <Label className="text-sm font-medium text-[#1A1A1A]">Required Backup Hours</Label>
                  <span className="mono text-sm font-semibold text-[#0061DD]">{form.backup_hours} hrs</span>
                </div>
                <Slider value={[form.backup_hours]} onValueChange={([v]) => update('backup_hours', v)} min={0.5} max={6} step={0.5} />
                <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>0.5 hr</span><span>6 hrs</span></div>
              </div>

              {/* Solar */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-stone-50">
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">Do you have a Solar installation?</p>
                  <p className="text-xs text-[#A8A29E]">We can integrate BESS with solar</p>
                </div>
                <Switch checked={form.has_solar} onCheckedChange={v => update('has_solar', v)} data-testid="quote-has-solar-switch" />
              </div>

              {form.has_solar && (
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">Solar Capacity (kW)</Label>
                  <Input type="number" value={form.solar_kw} onChange={e => update('solar_kw', parseFloat(e.target.value) || 0)} className="mt-1.5 h-12 rounded-xl" data-testid="quote-solar-kw-input" />
                </div>
              )}

              {/* Location */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">City</Label>
                  <Input value={form.city} onChange={e => update('city', e.target.value)} placeholder="e.g., Bangalore" className="mt-1.5 h-12 rounded-xl" data-testid="quote-city-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">Pincode</Label>
                  <Input value={form.pincode} onChange={e => update('pincode', e.target.value)} placeholder="e.g., 560024" className="mt-1.5 h-12 rounded-xl" data-testid="quote-pincode-input" />
                </div>
              </div>

              {/* Upload section */}
              {mode === 'upload' && (
                <div className="p-4 rounded-xl border-2 border-dashed border-stone-300 text-center" data-testid="quote-upload-section">
                  <FileText className="w-8 h-8 text-[#A8A29E] mx-auto mb-2" />
                  <p className="text-sm font-medium text-[#1A1A1A]">Upload electricity bills & DG logs</p>
                  <p className="text-xs text-[#A8A29E] mt-1">PDF, JPG, or PNG. Helps us provide a tighter quote.</p>
                  <Button type="button" variant="outline" className="mt-3 rounded-full text-sm" data-testid="quote-upload-btn">
                    Choose Files
                  </Button>
                </div>
              )}

              {/* Contact Info */}
              <div className="pt-4 border-t border-stone-200">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Your Contact Details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Name *</Label>
                    <Input value={form.contact_name} onChange={e => update('contact_name', e.target.value)} placeholder="Your name" className="mt-1.5 h-12 rounded-xl" data-testid="quote-name-input" required />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Company</Label>
                    <Input value={form.company} onChange={e => update('company', e.target.value)} placeholder="Company name" className="mt-1.5 h-12 rounded-xl" data-testid="quote-company-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Email *</Label>
                    <Input type="email" value={form.contact_email} onChange={e => update('contact_email', e.target.value)} placeholder="you@company.com" className="mt-1.5 h-12 rounded-xl" data-testid="quote-email-input" required />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Phone</Label>
                    <Input value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)} placeholder="+91 9876543210" className="mt-1.5 h-12 rounded-xl" data-testid="quote-phone-input" />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/20 gap-2" data-testid="quote-submit-btn">
                {submitting ? 'Processing...' : 'Get Recommendations'}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
